import React, { useState, useEffect } from 'react';
import Network from '../../../models/Network.js';
import AgencyListElement from '../list_elements/AgencyListElement.js';
import './Module.css';
import LoadingListElement from '../list_elements/LoadingListElement.js';
const key = process.env.REACT_APP_CLIENT_ACCESS_KEY;
const URLS = require("../../../urls.js");
const StatusCodes = require('../../../status-codes/status-codes.js');

const AgencyListModule = ({ user, isSmallScreen }) => {
    const [agenciesData, setAgenciesData] = useState(null);
    const [agencyUsers, setAgencyUsers] = useState({});
    const [userId, setUserId] = useState('');
    const [selectedAgencyId, setSelectedAgencyId] = useState('');
    const [isLinking, setIsLinking] = useState(false);
    const [linkMessage, setLinkMessage] = useState('');

    const getAllAgencies = async () => {
        const url = URLS.GET_ALL_AGENCIES;
        const dict = {
            uid: user.uid,
            CLIENT_ACCESS_KEY: key
        };

        let responseData;

        await Network.post(url, dict, (response) => {
            responseData = response.data;

            const extractedErrorCode = responseData.toString();
            const status = response.status;

            if (Network.checkValidStatusAndResponse(response)) {
                // success case
            } else if (status === StatusCodes.INTERNAL_SERVER_ERROR) {
                console.log("Server Error");
            } else {
                console.log("Unknown Error");
            }
        });

        return responseData;
    };

    const getAgencyUsers = async (agencyUid) => {
        const url = URLS.GET_AGENCY_USERS;
        const dict = {
            uid: user.uid,
            agency_uid: agencyUid,
            CLIENT_ACCESS_KEY: key
        };

        let responseData;

        await Network.post(url, dict, (response) => {
            responseData = response.data;

            const extractedErrorCode = responseData.toString();
            const status = response.status;

            if (Network.checkValidStatusAndResponse(response)) {
                // success case
            } else if (status === StatusCodes.INTERNAL_SERVER_ERROR) {
                console.log("Server Error");
            } else {
                console.log("Unknown Error");
            }
        });

        return responseData;
    };

    const linkUserToAgency = async () => {
        if (!userId.trim() || !selectedAgencyId) {
            setLinkMessage('Please enter both user ID and select an agency');
            return;
        }

        setIsLinking(true);
        setLinkMessage('');

        const url = URLS.LINK_USER_TO_AGENCY;
        const dict = {
            uid: user.uid,
            user_id: parseInt(userId.trim()),
            agency_id: parseInt(selectedAgencyId),
            CLIENT_ACCESS_KEY: key
        };

        try {
            await Network.post(url, dict, (response) => {
                const status = response.status;

                if (Network.checkValidStatusAndResponse(response)) {
                    setLinkMessage('User successfully linked to agency!');
                    setUserId('');
                    setSelectedAgencyId('');
                    
                    // Refresh the agencies data to update user counts
                    getAllAgencies()
                        .then((data) => {
                            setAgenciesData(data);
                        });
                } else if (status === StatusCodes.INTERNAL_SERVER_ERROR) {
                    setLinkMessage('Server Error - Please try again');
                } else {
                    setLinkMessage('Error linking user to agency');
                }
            });
        } catch (error) {
            setLinkMessage('Error linking user to agency');
            console.log('Error:', error);
        } finally {
            setIsLinking(false);
        }
    };

    const handleAgencyExpand = async (agencyUid) => {
        // Check if we already have the users for this agency
        if (!agencyUsers[agencyUid]) {
            try {
                const usersData = await getAgencyUsers(agencyUid);
                setAgencyUsers(prev => ({
                    ...prev,
                    [agencyUid]: usersData
                }));
            } catch (error) {
                console.log('Error fetching agency users:', error);
            }
        }
    };

    useEffect(() => {
        getAllAgencies()
            .then((data) => {
                setAgenciesData(data);
            })
            .catch((error) => {
                console.log('Error:', error);
            });
    }, [user.uid]);

    const createAgencyListElement = (agency) => {
        return (
            <AgencyListElement
                key={agency.uid}
                agency={agency}
                users={agencyUsers[agency.uid] || []}
                onExpand={() => handleAgencyExpand(agency.uid)}
            />
        );
    };

    const sortAgencies = (agencies) => {
        return [...agencies].sort((a, b) => {
            // First, sort by whether agency_name is null (non-null first)
            const aHasName = a.agency_name && a.agency_name.trim() !== '';
            const bHasName = b.agency_name && b.agency_name.trim() !== '';
            
            if (aHasName && !bHasName) return -1;
            if (!aHasName && bHasName) return 1;
            
            // If both have names or both don't have names, sort by uid ascending
            return a.uid - b.uid;
        });
    };

    return (
        <div className={isSmallScreen ? "standard-module-screen-small" : "standard-module"}>
            {/* User Association Interface */}
            <div className="user-association-section">
                <h3>Link User to Agency</h3>
                <div className="user-association-form">
                    <div className="form-group">
                        <label htmlFor="userId">User ID:</label>
                        <input
                            type="number"
                            id="userId"
                            value={userId}
                            onChange={(e) => setUserId(e.target.value)}
                            placeholder="Enter user ID"
                            className="standard-input"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="agencySelect">Agency:</label>
                        <select
                            id="agencySelect"
                            value={selectedAgencyId}
                            onChange={(e) => setSelectedAgencyId(e.target.value)}
                            className="standard-input"
                        >
                            <option value="">Select an agency</option>
                            {agenciesData && sortAgencies(agenciesData).map((agency) => (
                                <option key={agency.uid} value={agency.uid}>
                                    {agency.agency_name || `Agency ${agency.uid}`} ({agency.agency_number})
                                </option>
                            ))}
                        </select>
                    </div>
                    <button
                        onClick={linkUserToAgency}
                        disabled={isLinking}
                        className="link-button"
                    >
                        {isLinking ? 'Linking...' : 'Link User to Agency'}
                    </button>
                </div>
                {linkMessage && (
                    <div className={`link-message ${linkMessage.includes('successfully') ? 'success' : 'error'}`}>
                        {linkMessage}
                    </div>
                )}
            </div>

            {/* Agencies List */}
            {agenciesData ? (
                sortAgencies(agenciesData).map((agency) => 
                    createAgencyListElement(agency)
                )
            ) : (
                <LoadingListElement />
            )}
        </div>
    );
};

export default AgencyListModule; 